import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

// Define the type for our simplified user context
type UserContextType = {
  userId: string | null;
  email: string | null;
  role: string | null;
  companyId: number | null;
  name: string | null;
  loading: boolean;
  error: Error | null;
  refetchUser: () => Promise<void>;
};

// Create the context
const UserContext = createContext<UserContextType | undefined>(undefined);

/**
 * Provider component that fetches and shares user data
 * Wrap your app with this component to provide user data to all child components
 */
export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [userId, setUserId] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [name, setName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get authenticated user from Supabase
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        setUserId(null);
        setEmail(null);
        setRole(null);
        setCompanyId(null);
        setName(null);
        setLoading(false);
        return;
      }

      // Identity comes from the session itself, so a transient failure of the
      // profile query below never makes an authenticated user look logged out.
      setUserId(session.user.id);
      setEmail(session.user.email ?? null);

      // Get user record with only the required fields. The users view can
      // return multiple rows when the same email exists in employees for more
      // than one company, so take at most one.
      const { data: user, error: userError } = await supabase
        .from('users')
        .select('user_id, email, role, company_id, name')
        .eq('user_id', session.user.id)
        .limit(1)
        .maybeSingle();

      if (userError) throw userError;

      setRole(user?.role || null);
      setCompanyId(user?.company_id || null);
      setName(user?.name || null);

    } catch (err) {
      console.error('Error fetching user data:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch user data'));
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch on mount
  useEffect(() => {
    fetchUserData();

    // Subscribe to auth changes (login/logout)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'TOKEN_REFRESHED') {
        // Nothing about the profile changes on a token refresh; refetching
        // would flash the app-wide loading state.
        return;
      }
      if (session) {
        fetchUserData();
      } else {
        setUserId(null);
        setEmail(null);
        setRole(null);
        setCompanyId(null);
        setName(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  return (
    <UserContext.Provider value={{
      userId,
      email,
      role,
      companyId,
      name,
      loading,
      error,
      refetchUser: fetchUserData
    }}>
      {children}
    </UserContext.Provider>
  );
};

/**
 * Custom hook to use the user context
 * Use this in your components to access user data
 */
export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};